import { useState, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { AD_TYPES } from "components/core/blocks/AdPlan/constants";
import { useParams } from "react-router";
import { useDates } from "dates/DatesContext";
import AdTypeChart from "./AdTypeChart";
import AdTypeCards from "./AdTypeCards";
import Loading from "components/core/blocks/Loading";
import { Button, ButtonGroup } from "@material-ui/core";
import useAdTypeData from "./useAdTypeData";

export const adTypesByChannel = {
  amazon: [
    AD_TYPES.SPONSORED_PRODUCT,
    AD_TYPES.SPONSORED_PRODUCT_AUTO,
    AD_TYPES.SPONSORED_BRAND,
    AD_TYPES.SPONSORED_BRAND_V2,
    AD_TYPES.SPONSORED_BRAND_VIDEO,
    AD_TYPES.SPONSORED_DISPLAY,
    AD_TYPES.SPONSORED_DISPLAY_V2,
  ],
  walmart: [
    AD_TYPES.SPONSORED_PRODUCT_WALMART,
    AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART,
    AD_TYPES.SPONSORED_BRAND_WALMART,
    AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART,
  ],
};

const CHART_METRIC_OPTIONS = [
  {
    value: "ad_sales",
    label: "Ad Sales",
    format: "currency",
  },
  {
    value: "cost",
    label: "Ad Spend",
    format: "currency",
  },
  {
    value: "active_ad_count",
    label: "# of Active Plans",
    format: "number",
  },
  {
    value: "clicks",
    label: "Clicks",
    format: "number",
  },
  {
    value: "impressions",
    label: "Impressions",
    format: "number",
  },
];

const AdTypeSection = ({ adTypeFilter, setAdTypeFilter, additionalFilters, search }) => {
  const [pieChartMetric, setPieChartMetric] = useState(CHART_METRIC_OPTIONS[0]);

  const { start, end } = useDates();
  const { id: categoryId } = useParams();
  const { data: adTypeData, isLoading } = useAdTypeData(categoryId, true, additionalFilters, search);

  const pieChartData = useMemo(() => {
    return (
      adTypeData
        ?.map((type) => {
          return {
            ad_type: type.ad_type,
            color: type.color,
            y: type[pieChartMetric.value],
          };
        })
        ?.filter((type) => type.y) ?? []
    );
  }, [adTypeData, pieChartMetric]);

  if (isLoading) {
    return <Loading fullPage={false} height={500} />;
  }

  return (
    <Row>
      {!adTypeData?.length ?
        <Col className="text-center ">
          <h4>No Data Available</h4>
          <p>This section will be populated once the first media plan is created on this account.</p>
        </Col>
      : <>
          <Col xs={{ span: 12, order: 2 }} xl={{ span: 6, order: 1 }}>
            <div className="pb-5 d-flex justify-content-between">
              <div>
                <h3
                  style={{
                    marginTop: "0rem",
                    marginBottom: "0.2rem",
                  }}
                >
                  {pieChartMetric?.label} By Media Plan Type
                </h3>
                <small>
                  {start.format("MMMM Do YYYY")}
                  {" - "}
                  {end.format("MMMM Do YYYY")}
                </small>
              </div>
            </div>

            <div className="py-5">
              <AdTypeChart data={pieChartData} format={pieChartMetric.format} />
            </div>
            <div className="d-flex justify-content-center align-items-center pt-5 mt-5">
              <ButtonGroup>
                {CHART_METRIC_OPTIONS?.map((group) => {
                  const { value, label } = group;
                  return (
                    <Button
                      key={value}
                      onClick={() => setPieChartMetric(group)}
                      className="fs-6"
                      variant={pieChartMetric.value === value ? "contained" : null}
                      color={pieChartMetric.value === value ? "secondary" : null}
                    >
                      {label}
                    </Button>
                  );
                })}
              </ButtonGroup>
            </div>
          </Col>
          <Col xs={{ span: 12, order: 1 }} xl={{ span: 6, order: 2 }}>
            <AdTypeCards selectedCard={adTypeFilter} setSelectedCard={setAdTypeFilter} data={adTypeData} />
          </Col>
        </>
      }
    </Row>
  );
};

export default AdTypeSection;
